import {
  AttachmentList,
  FileSize,
  FileType,
  Modal,
  RichText,
  Spacer,
} from '@porsche-code/kado-ui';
import { PButton, PText } from '@porsche-design-system/components-react';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import saveAs from 'file-saver';
import { ClientError } from 'graphql-request';
import { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

import { NAMESPACES } from '@/config/i18n';
import { usePersonContext } from '@/context';
import {
  Attachment,
  DownloadReference,
  DownloadType,
  useCreateDownloadUrlMutation,
} from '@/graphql';

export const Comment = ({
  comment,
}: {
  comment: {
    id: string;
    author: { id: number; name: string };
    createdAt: string;
    text: string;
    attachments?: Attachment[] | null;
  };
}) => {
  const dateFormat =
    usePersonContext().state.settings?.dateFormat ?? 'YYYY/MM/DD';
  const hourFormat = usePersonContext().state.settings?.is24HourFormat
    ? 'HH:mm'
    : 'HH:mm a';

  return (
    <div>
      <PText>
        {dayjs(comment.createdAt).format(`${dateFormat} ${hourFormat}`)} —{' '}
        {comment.author.name}
      </PText>

      <Spacer mb="sm" />
      <RichText value={JSON.parse(comment.text)} />

      <Spacer mt="md" />
      <AttachmentList>
        {comment.attachments?.map((attachment) => (
          <AttachmentItem key={attachment.id} attachment={attachment} />
        ))}
      </AttachmentList>
    </div>
  );
};

const AttachmentItem = ({
  attachment,
}: {
  attachment: {
    id: string;
    name: string;
    type: string;
    size: number;
    createdAt: string;
  };
}) => {
  const { t } = useTranslation(NAMESPACES);

  const { mutateAsync: createDownloadUrl } = useCreateDownloadUrlMutation();

  const createAttachmentDownloadUrl = async (id: string) => {
    const { createDownloadUrl: downloadUrl } = await createDownloadUrl({
      id,
      type: DownloadType.Attachment,
      reference: DownloadReference.Dashboard,
    });

    return downloadUrl;
  };

  const downloadAttachmentMutation = useMutation<void, ClientError, string>({
    mutationFn: async (attachmentId) => {
      const downloadUrl = await createAttachmentDownloadUrl(attachmentId);

      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      saveAs(blob, attachment.name);
    },
  });

  return (
    <>
      <AttachmentList.Item
        key={attachment.id}
        fileType={attachment.type}
        isLoading={downloadAttachmentMutation.isPending}
        onClick={() => {
          downloadAttachmentMutation.reset();
          downloadAttachmentMutation.mutate(attachment.id);
        }}
        canPreview={attachment.type.startsWith('image/')}
        previewHeading={attachment.name}
        onPreview={async () => {
          try {
            const url = await createAttachmentDownloadUrl(attachment.id);
            return { url };
          } catch (error: unknown) {
            if (error instanceof ClientError) {
              const message =
                error.response?.errors?.[0]?.message ?? error.message;
              throw new Error(t(message as ParseKeys), { cause: error });
            }

            throw new Error(t('common:upload.securityInspection.fallback'), {
              cause: error,
            });
          }
        }}
        i18n={{
          cancel: t('common:action.cancel'),
          download: t('common:action.download'),
        }}
      >
        <AttachmentList.Item.Meta
          label={t('common:upload.uploadDateShort')}
          value={
            dayjs(attachment.createdAt).isValid()
              ? t('common:upload.uploadDateDate', {
                  date: new Date(attachment.createdAt),
                })
              : null
          }
        />
        <AttachmentList.Item.Meta
          label={t('common:upload.fileSize')}
          value={<FileSize value={attachment.size} />}
        />
        <AttachmentList.Item.Meta
          label={t('common:upload.fileType')}
          value={<FileType value={attachment.type} hideIcon />}
        />
      </AttachmentList.Item>

      <Modal
        open={downloadAttachmentMutation.isError}
        onDismiss={() => downloadAttachmentMutation.reset()}
      >
        <Modal.Header>
          {t('common:upload.securityInspection.headline')}
        </Modal.Header>
        <PText>
          {downloadAttachmentMutation.error instanceof ClientError
            ? t(
                (downloadAttachmentMutation.error.response?.errors?.[0]
                  ?.message ??
                  downloadAttachmentMutation.error.message) as ParseKeys,
              )
            : t('common:upload.securityInspection.fallback')}
        </PText>
        <Modal.Footer sticky>
          <PButton
            type="button"
            variant="secondary"
            icon="close"
            onClick={() => downloadAttachmentMutation.reset()}
          >
            {t('common:action.cancel')}
          </PButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};
